import { createContext, useContext, type ReactNode } from 'react';

/** Href builders for the catalog list + detail pages. The same catalog UI is
 *  mounted on the marketing site and inside the dashboard, which live at
 *  different paths - this lets the shared components link to the right place. */
export interface CatalogLinks {
  /** the catalog list page */
  catalog: string;
  /** an API detail page */
  api: (apiId: string) => string;
  /** an endpoint detail page */
  endpoint: (apiId: string, endpointId: string) => string;
}

const SITE_LINKS: CatalogLinks = {
  catalog: '/catalog',
  api: (id) => `/api/${id}`,
  endpoint: (apiId, endpointId) => `/api/${apiId}/${endpointId}`,
};

const DASHBOARD_LINKS: CatalogLinks = {
  catalog: '/dashboard/catalog',
  api: (id) => `/dashboard/catalog/${id}`,
  endpoint: (apiId, endpointId) => `/dashboard/catalog/${apiId}/${endpointId}`,
};

// Default to the marketing paths so the public catalog needs no provider.
const CatalogLinksContext = createContext<CatalogLinks>(SITE_LINKS);

/** The active catalog link builders (marketing paths unless a provider overrides). */
export const useCatalogLinks = (): CatalogLinks => useContext(CatalogLinksContext);

/** Switches the catalog's internal links to the marketing site or the dashboard. */
export const CatalogLinksProvider = ({
  base,
  children,
}: {
  base: 'site' | 'dashboard';
  children: ReactNode;
}) => (
  <CatalogLinksContext.Provider value={base === 'dashboard' ? DASHBOARD_LINKS : SITE_LINKS}>
    {children}
  </CatalogLinksContext.Provider>
);
